import { ExternalCalendarEvent } from '@griply/types';
import { useRef } from 'react';

import { useDebounceValue } from './use-debounce-value';

export const externalCalendarEventsQueryKey = 'external-calendar-events';

const CHANGED_AT_DEBOUNCE_MS = 3000;
const CHANGED_AT_MAX_WAIT_MS = 15_000;

export type FetchExternalCalendarEvents = (input: {
  startDate: Date;
  endDate: Date;
  timezone: string;
}) => Promise<ExternalCalendarEvent[]>;

export type UseExternalCalendarEventsQueryConfigOptions = {
  startDate: string;
  endDate: string;
  timezone: string;
  changedAtMillis: number;
  fetchEvents: FetchExternalCalendarEvents;
  onFetchError: (error: unknown) => void;
};

type CreateQueryFnOptions = Omit<
  UseExternalCalendarEventsQueryConfigOptions,
  'changedAtMillis'
> & {
  lastSuccessfulData: { current: ExternalCalendarEvent[] };
};

export const createExternalCalendarEventsQueryFn =
  ({
    startDate,
    endDate,
    timezone,
    fetchEvents,
    onFetchError,
    lastSuccessfulData,
  }: CreateQueryFnOptions) =>
  async (): Promise<ExternalCalendarEvent[]> => {
    try {
      const data = await fetchEvents({
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        timezone,
      });
      lastSuccessfulData.current = data;
      return data;
    } catch (error) {
      onFetchError(error);
      return lastSuccessfulData.current;
    }
  };

export const useExternalCalendarEventsQueryConfig = ({
  startDate,
  endDate,
  timezone,
  changedAtMillis,
  fetchEvents,
  onFetchError,
}: UseExternalCalendarEventsQueryConfigOptions) => {
  const debouncedChangedAt = useDebounceValue(
    changedAtMillis,
    CHANGED_AT_DEBOUNCE_MS,
    { maxWait: CHANGED_AT_MAX_WAIT_MS },
  );
  const lastSuccessfulData = useRef<ExternalCalendarEvent[]>([]);

  return {
    queryKey: [
      externalCalendarEventsQueryKey,
      startDate,
      endDate,
      timezone,
      debouncedChangedAt,
    ],
    queryFn: createExternalCalendarEventsQueryFn({
      startDate,
      endDate,
      timezone,
      fetchEvents,
      onFetchError,
      lastSuccessfulData,
    }),
    staleTime: Infinity,
    placeholderData: (previous?: ExternalCalendarEvent[]) => previous ?? [],
  };
};
